import { useRoomStore } from '@/store/roomStore';
import { useCallStore } from '@/store/callStore';
import { Crown, Users, ArrowRightLeft, Mic, MicOff, Video, VideoOff, UserX } from 'lucide-react';

interface ParticipantListProps {
  onTransferHost?: (newHostId: string) => void;
  onRemoveParticipant?: (participantId: string) => void;
}

export function ParticipantList({ onTransferHost, onRemoveParticipant }: ParticipantListProps) {
  const { room, participantId } = useRoomStore();
  const { localMicEnabled, localCamEnabled, peerMediaStates } = useCallStore();

  if (!room) return null;

  const isCurrentHost = room.hostId === participantId;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-cinema-300">
        <span className="flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-lamp" />
          <span>People in Room</span>
        </span>
        <span className="text-cinema-400 font-bold">({room.participants.length})</span>
      </div>

      <div className="space-y-1.5">
        {room.participants.map((p) => {
          const isMe = p.id === participantId;
          const isHost = p.role === 'host';

          const micOn = isMe ? localMicEnabled : (peerMediaStates[p.id]?.micEnabled ?? p.micEnabled ?? false);
          const camOn = isMe ? localCamEnabled : (peerMediaStates[p.id]?.camEnabled ?? p.camEnabled ?? false);

          const statusColor =
            p.connectionState === 'connected'
              ? 'bg-emerald-500'
              : p.connectionState === 'connecting'
              ? 'bg-amber-400 animate-pulse'
              : 'bg-cinema-500';

          return (
            <div
              key={p.id}
              className="flex items-center justify-between p-2 rounded-md bg-cinema-950/80 border border-cinema-700/60 text-xs group"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="relative flex-shrink-0">
                  <div className="w-6 h-6 rounded bg-cinema-800 border border-cinema-700 flex items-center justify-center text-[11px] font-bold text-lamp">
                    {p.name.charAt(0).toUpperCase()}
                  </div>
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-cinema-900 ${statusColor}`}
                    title={`Status: ${p.connectionState || 'online'}`}
                  />
                </div>

                <div className="truncate flex items-center gap-1.5">
                  <span className="text-cinema-100 font-medium truncate">{p.name}</span>
                  {isMe && (
                    <span className="text-[10px] text-cinema-400 bg-cinema-800 px-1 py-0.5 rounded">
                      You
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Voice / Video status indicators */}
                <div className="flex items-center gap-1 bg-cinema-900/80 px-1.5 py-0.5 rounded border border-cinema-800">
                  <span title={micOn ? 'Microphone on' : 'Microphone muted'}>
                    {micOn ? (
                      <Mic className="w-3 h-3 text-lamp" />
                    ) : (
                      <MicOff className="w-3 h-3 text-cinema-500" />
                    )}
                  </span>
                  <span title={camOn ? 'Camera on' : 'Camera off'}>
                    {camOn ? (
                      <Video className="w-3 h-3 text-lamp" />
                    ) : (
                      <VideoOff className="w-3 h-3 text-cinema-500" />
                    )}
                  </span>
                </div>

                {isHost ? (
                  <span
                    title="Room Host"
                    className="inline-flex items-center gap-1 text-[10px] text-lamp bg-lamp/10 border border-lamp/30 px-1.5 py-0.5 rounded font-medium"
                  >
                    <Crown className="w-3 h-3 text-lamp" />
                    <span>Host</span>
                  </span>
                ) : (
                  <>
                    <span className="text-cinema-400 text-xs">Guest</span>
                    {isCurrentHost && onTransferHost && (
                      <button
                        onClick={() => onTransferHost(p.id)}
                        title={`Transfer host to ${p.name}`}
                        className="opacity-0 group-hover:opacity-100 p-1 text-cinema-400 hover:text-lamp hover:bg-cinema-800 rounded transition-all focus-visible:opacity-100"
                        aria-label={`Transfer host to ${p.name}`}
                      >
                        <ArrowRightLeft className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {isCurrentHost && onRemoveParticipant && !isMe && (
                      <button
                        onClick={() => onRemoveParticipant(p.id)}
                        title={`Remove ${p.name} from room`}
                        className="opacity-0 group-hover:opacity-100 p-1 text-cinema-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-all focus-visible:opacity-100"
                        aria-label={`Remove ${p.name} from room`}
                      >
                        <UserX className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
